import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { SelectWithFreeInput } from "../common/SelectWithFreeInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DogSelector } from "./DogSelector";
import { User } from "../../types/entities";
import { getDogActivityTrainerOptions } from "../../utils/trail/getDogActivityTrainerOptions";
import { useAuth } from "../../contexts/AuthContext";
import { Dog } from "../../types";
import { GpxSection } from "./GpxSection";
import { MapSection } from "./MapSection";
import { parseGPXFile } from "../../utils/gpxParser";
import {
  calculateDistance,
  calculateDuration,
  calculateCenter,
} from "../../utils/gpx";

// Data interface for getting/setting form data
export interface MantrailingFormData {
  selectedDogId: string;
  handlerName: string;
  trainer: string;
  trailType: string;
  startType: "knowing" | "blind";
  delay: number;
  trainerComment: string;
  dogGpxData: any;
  userGpxData: any;
  locationCoordinate: [number, number];
}

// Handle interface for parent component
export interface MantrailingFormHandle {
  getData: () => MantrailingFormData;
  setData: (data: Partial<MantrailingFormData>) => void;
}

interface MantrailingFormProps {
  user: User | null;
  dogsPlaceholder?: Dog[];
  location: string;
  setLocation: (location: string) => void;
  onDistanceChange: (distance: number) => void;
  onDurationChange: (duration: number) => void;
  onLocationCoordinateChange: (coords: [number, number]) => void;
}

export const MantrailingForm = forwardRef<
  MantrailingFormHandle,
  MantrailingFormProps
>(
  (
    {
      user,
      dogsPlaceholder,
      location,
      setLocation,
      onDistanceChange,
      onDurationChange,
      onLocationCoordinateChange,
    },
    ref,
  ) => {
    const isTrainer = user?.role.includes("trainer");
    const { dogs } = useAuth();

    // Internal state - all mantrailing-specific fields
    const [selectedDogId, setSelectedDogId] = useState<string>("");
    const [handlerName, setHandlerName] = useState("");
    const [trainer, setTrainer] = useState("");
    const [trainerComment, setTrainerComment] = useState("");
    const [trailType, setTrailType] = useState("");
    const [startType, setStartType] = useState<"knowing" | "blind">("knowing");
    const [delay, setDelay] = useState(0);
    const [delayInMinutes, setDelayInMinutes] = useState<number | string>("");

    // GPX state (now internal to mantrailing)
    const [dogGpxFile, setDogGpxFile] = useState<File | null>(null);
    const [userGpxFile, setUserGpxFile] = useState<File | null>(null);
    const [dogGpxData, setDogGpxData] = useState<any>(null);
    const [userGpxData, setUserGpxData] = useState<any>(null);
    const [gpxError, setGpxError] = useState("");
    const [previewDogPath, setPreviewDogPath] = useState<
      [number, number][] | null
    >(null);
    const [previewUserPath, setPreviewUserPath] = useState<
      [number, number][] | null
    >(null);
    const [locationCoordinate, setLocationCoordinate] = useState<
      [number, number]
    >([46.2563, 5.6554]);

    // Sync delay state (seconds <-> minutes)
    useEffect(() => {
      setDelayInMinutes(delay > 0 ? delay / 60 : "");
    }, [delay]);

    const handleDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setDelayInMinutes(value);
      if (value === "") {
        setDelay(0);
      } else {
        const minutes = Number(value);
        if (!isNaN(minutes) && minutes >= 0) {
          setDelay(minutes * 60);
        }
      }
    };

    // GPX Upload Handler
    const handleGPXUpload =
      (type: "dog" | "user") =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith(".gpx")) {
          setGpxError("Veuillez sélectionner un fichier GPX valide");
          return;
        }

        try {
          const content = await file.text();
          const gpxData = parseGPXFile(content);

          if (!gpxData) {
            setGpxError("Erreur lors de l'analyse du fichier GPX");
            return;
          }

          if (gpxData.center) {
            setLocationCoordinate(gpxData.center as [number, number]);
            onLocationCoordinateChange(gpxData.center as [number, number]);
          }

          if (type === "dog") {
            setDogGpxFile(file);
            setDogGpxData(gpxData);
            onDistanceChange(gpxData.distance);
          } else {
            setUserGpxFile(file);
            setUserGpxData(gpxData);
          }

          if (gpxData.duration) {
            onDurationChange(gpxData.duration);
          }

          setGpxError("");
        } catch (error) {
          setGpxError("Erreur lors de la lecture du fichier GPX");
          console.error(error);
        }
      };

    const handleRemoveGPX = (type: "dog" | "user") => () => {
      if (type === "dog") {
        setDogGpxFile(null);
        setDogGpxData(null);
        setPreviewDogPath(null);
      } else {
        setUserGpxFile(null);
        setUserGpxData(null);
        setPreviewUserPath(null);
      }
      setGpxError("");
    };

    const handlePreviewDogPath = (
      previewPath: [number, number][],
      newTimestamps?: (string | Date)[],
    ) => {
      setPreviewDogPath(previewPath);
    };

    const handleUpdateDogPath = (
      newPath: [number, number][],
      newTimestamps?: (string | Date)[],
    ) => {
      if (!dogGpxData) return;

      const newDistance = calculateDistance(newPath);
      const center = calculateCenter(newPath);

      setDogGpxData({
        ...dogGpxData,
        path: newPath,
        distance: newDistance,
        center,
        timestamps: newTimestamps,
      });

      onDistanceChange(newDistance);
      onDurationChange(calculateDuration(newTimestamps));
      setPreviewDogPath(null);
    };

    const handlePreviewUserPath = (
      previewPath: [number, number][],
      newTimestamps?: (string | Date)[],
    ) => {
      setPreviewUserPath(previewPath);
    };

    const handleUpdateUserPath = (
      newPath: [number, number][],
      newTimestamps?: (string | Date)[],
    ) => {
      if (!userGpxData) return;

      const newDistance = calculateDistance(newPath);
      const center = calculateCenter(newPath);

      setUserGpxData({
        ...userGpxData,
        path: newPath,
        distance: newDistance,
        center,
        timestamps: newTimestamps,
      });

      setPreviewUserPath(null);
    };

    const handleLocationCoordinateChange = (coords: [number, number]) => {
      setLocationCoordinate(coords);
      onLocationCoordinateChange(coords);
    };

    // Filter trainers from the selected dog's populated trainers array
    const availableTrainersForDogOptions = useMemo(() => {
      if (!selectedDogId || !dogs || dogs.length === 0) {
        return [];
      }
      return getDogActivityTrainerOptions(
        dogs.find((dog) => dog._id === selectedDogId),
        'mantrailing',
      );
    }, [selectedDogId, dogs]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      getData: (): MantrailingFormData => ({
        selectedDogId,
        handlerName,
        trainer,
        trailType,
        startType,
        delay,
        trainerComment,
        dogGpxData,
        userGpxData,
        locationCoordinate,
      }),
      setData: (data: Partial<MantrailingFormData>) => {
        if (data.selectedDogId !== undefined)
          setSelectedDogId(data.selectedDogId);
        if (data.handlerName !== undefined) setHandlerName(data.handlerName);
        if (data.trainer !== undefined) setTrainer(data.trainer);
        if (data.trailType !== undefined) setTrailType(data.trailType);
        if (data.startType !== undefined) setStartType(data.startType);
        if (data.delay !== undefined) setDelay(data.delay);
        if (data.trainerComment !== undefined)
          setTrainerComment(data.trainerComment);
        if (data.dogGpxData !== undefined) setDogGpxData(data.dogGpxData);
        if (data.userGpxData !== undefined) setUserGpxData(data.userGpxData);
        if (data.locationCoordinate !== undefined)
          setLocationCoordinate(data.locationCoordinate);
      },
    }));

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <DogSelector
              value={selectedDogId}
              onChange={setSelectedDogId}
              required
              dogPlaceHolder={dogsPlaceholder}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="handlerName">Nom du maître *</Label>
            <Input
              id="handlerName"
              value={handlerName}
              onChange={(e) => setHandlerName(e.target.value)}
              required
            />
          </div>
        </div>
        <SelectWithFreeInput
          id="trainer"
          label="Formateur"
          options={availableTrainersForDogOptions}
          value={trainer}
          onChange={setTrainer}
          placeholder="Sélectionnez un formateur"
          freeInputPlaceholder="Saisissez le nom du formateur"
        />
        {isTrainer && (
          <div className="space-y-2">
            <Label htmlFor="trainerComment">Commentaire du formateur</Label>
            <Textarea
              id="trainerComment"
              value={trainerComment}
              onChange={(e) => setTrainerComment(e.target.value)}
              placeholder="Ajoutez un commentaire visible uniquement par les formateurs..."
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="trailType">Type de piste</Label>
          <Input
            id="trailType"
            value={trailType}
            onChange={(e) => setTrailType(e.target.value)}
            placeholder="Ex: Milieu urbain, forêt..."
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="startType">Type de départ</Label>
          <Select
            value={startType}
            onValueChange={(value) =>
              setStartType(value as "knowing" | "blind")
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="knowing">Départ visuel / Knowing</SelectItem>
              <SelectItem value="blind">Départ à l'aveugle / Blind</SelectItem>
              <SelectItem value="double blind">
                Double aveugle / Double blind
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="delay">Délai (minutes)</Label>
          <Input
            id="delay"
            type="number"
            value={delayInMinutes}
            onChange={handleDelayChange}
            placeholder="0"
            min="0"
          />
        </div>

        {/* GPX Section - now part of MantrailingForm */}
        <GpxSection
          category="mantrailing"
          dogGpxFile={dogGpxFile}
          userGpxFile={userGpxFile}
          dogGpxData={dogGpxData}
          userGpxData={userGpxData}
          gpxError={gpxError}
          handleGPXUpload={handleGPXUpload}
          handleRemoveGPX={handleRemoveGPX}
          handleUpdateDogPath={handleUpdateDogPath}
          handlePreviewDogPath={handlePreviewDogPath}
          handleUpdateUserPath={handleUpdateUserPath}
          handlePreviewUserPath={handlePreviewUserPath}
        />

        {/* Map Section - now part of MantrailingForm */}
        <MapSection
          category="mantrailing"
          dogGpxData={dogGpxData}
          userGpxData={userGpxData}
          locationCoordinate={locationCoordinate}
          previewDogPath={previewDogPath}
          previewUserPath={previewUserPath}
          setLocation={setLocation}
          setLocationCoordinate={handleLocationCoordinateChange}
          location={location}
        />
      </>
    );
  },
);

MantrailingForm.displayName = "MantrailingForm";
